/**
 * Complex matrices — rectangular Mᵣ,ₖ(ℂ) and the square ring Mₙ(ℂ).
 *
 * Our matrix types are *interchange types with type safety*, not a compute
 * engine. The runtime value of a `Matrix<Rows, Cols, T>` IS a mathjs `Matrix`;
 * the type adds a compile-time phantom brand carrying the shape and element type.
 * Every calculation is therefore a *direct* mathjs call — `cmatMul` is mathjs
 * `multiply`, `cmatExp` is mathjs `expm`, and so on — with no per-operation
 * marshaling: a whole chain stays in mathjs space. Conversion to/from the plain
 * `[re, im]` tuple form happens only at the boundary, via `complexMatrix` /
 * `cmatFromReal` (in) and `toTuples` (out).
 *
 * `SquareMatrix<N, T> = Matrix<N, N, T>` and `ComplexMatrix<N>` is the complex
 * square case; `Complex2x2` is `ComplexMatrix<2>`. Shape-respecting operations
 * (add/sub/scale/equal, the (r×k)·(k×c) product, transpose, conjugate transpose)
 * accept any rectangular matrix; operations that require a square matrix (trace,
 * determinant, exponential, eigenvalues, Lie bracket) check squareness at
 * runtime. None are restricted to the invertible group GL(n,ℂ): the carrier is
 * every square complex matrix, invertible or singular — invertibility is the
 * runtime predicate `cmatIsInvertible`, never a type-level constraint.
 *
 * (TypeScript has no operator overloading, so the surface is function calls
 * rather than `+`/`*`. The general eigensolver and matrix exponential come from
 * mathjs, Apache-2.0.)
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  add,
  complex,
  ctranspose,
  det,
  eigs,
  expm,
  isMatrix,
  matrix,
  multiply,
  norm,
  subtract,
  trace,
  transpose,
  type Complex as MathComplex,
  type Matrix as MathMatrix,
} from "mathjs";

import { cMod, type Complex } from "./complex.js";

// ── Shape-indexed matrix types (brands over a mathjs Matrix) ───

declare const shapeBrand: unique symbol;

/**
 * A `Rows`×`Cols` matrix over element type `T`. The runtime value is a mathjs
 * `Matrix`; `[shapeBrand]` is a phantom (never present at runtime) that records
 * the shape and element type for the compiler. `Matrix<number, number, T>` is an
 * unknown-shape matrix.
 */
export type Matrix<Rows extends number, Cols extends number, T> = MathMatrix & {
  readonly [shapeBrand]?: readonly [Rows, Cols, T];
};

/** An N×N square matrix over element type `T` — the `Rows = Cols = N` case. */
export type SquareMatrix<N extends number, T> = Matrix<N, N, T>;

/**
 * A complex matrix — an element of Mᵣ,ₖ(ℂ). Rectangular by default and not
 * assumed square: bare `ComplexMatrix` is `Matrix<number, number, Complex>`, and
 * `ComplexMatrix<R, C>` is an R×C matrix. The one-argument form `ComplexMatrix<N>`
 * defaults `Cols` to `Rows`, giving the square N×N case as a convenience (the
 * same type as `SquareMatrix<N, Complex>`). Never constrained to invertibility.
 */
export type ComplexMatrix<Rows extends number = number, Cols extends number = Rows> = Matrix<
  Rows,
  Cols,
  Complex
>;

/**
 * A 2×2 complex matrix. A thin alias for `ComplexMatrix<2>`, kept for the
 * GL(2,ℂ) tangent-embedding callers.
 */
export type Complex2x2 = ComplexMatrix<2>;

// ── Boundary helpers (the only [re,im] ⇄ mathjs conversions) ───

/** Convert one mathjs scalar (real or complex) into the `[re, im]` form. */
function fromMathScalar(z: number | MathComplex): Complex {
  return typeof z === "number" ? [z, 0] : [z.re, z.im];
}

/**
 * Re-attach a static shape to a mathjs result. A zero-cost type assertion — the
 * value already IS a mathjs `Matrix`; nothing is recomputed or copied.
 */
function shaped<Rows extends number, Cols extends number>(
  value: unknown,
): Matrix<Rows, Cols, Complex> {
  return value as Matrix<Rows, Cols, Complex>;
}

/** The [rows, cols] of a mathjs matrix, after checking it is two-dimensional. */
function dimsOf(m: MathMatrix): readonly [number, number] {
  const size = m.size();
  const rows = size[0];
  const cols = size[1];
  if (size.length !== 2 || rows === undefined || cols === undefined) {
    throw new RangeError("complex-matrix: expected a two-dimensional matrix");
  }
  return [rows, cols];
}

/** The order n of an n×n matrix, after checking it is genuinely square. */
function squareOrderOf(m: MathMatrix): number {
  const [rows, cols] = dimsOf(m);
  if (rows !== cols) {
    throw new RangeError(`complex-matrix: expected a square matrix, got ${rows}×${cols}`);
  }
  return rows;
}

// ── Constructors and extraction (boundary only) ────────────────

/** Build a complex matrix from `[re, im]` tuples (the interchange form). */
export function complexMatrix(
  rows: readonly (readonly Complex[])[],
): Matrix<number, number, Complex> {
  return shaped(matrix(rows.map((row) => row.map((z) => complex(z[0], z[1])))));
}

/** Build a complex matrix from a real (possibly rectangular) grid (imaginary parts zero). */
export function cmatFromReal(
  real: readonly (readonly number[])[],
): Matrix<number, number, Complex> {
  return shaped(matrix(real.map((row) => row.map((x) => complex(x, 0)))));
}

/** The n×n complex identity matrix. */
export function cmatIdentity<N extends number>(n: N): ComplexMatrix<N> {
  const rows: MathComplex[][] = [];
  for (let i = 0; i < n; i++) {
    const row: MathComplex[] = [];
    for (let j = 0; j < n; j++) {
      row.push(complex(i === j ? 1 : 0, 0));
    }
    rows.push(row);
  }
  return shaped<N, N>(matrix(rows));
}

/** Read a complex matrix out into `[re, im]` tuples (the interchange form). */
export function toTuples(m: Matrix<number, number, Complex>): Complex[][] {
  dimsOf(m); // enforce the 2-D invariant with the module's error (a 1-D matrix has no rows of entries)
  const rows = m.toArray() as readonly (readonly (number | MathComplex)[])[];
  return rows.map((row) => row.map(fromMathScalar));
}

/** The shape [rows, cols] of a matrix, recovered at the value level. */
export function cmatShape<R extends number, C extends number>(
  a: Matrix<R, C, Complex>,
): readonly [R, C] {
  const [rows, cols] = dimsOf(a);
  return [rows as R, cols as C];
}

/** The dimension n of an n×n complex matrix, recovered at the value level. */
export function cmatDimension<N extends number>(a: SquareMatrix<N, Complex>): N {
  return squareOrderOf(a) as N;
}

// ── Arithmetic (direct mathjs calls) ───────────────────────────

/** Matrix addition A + B (entrywise; A and B share a shape). */
export function cmatAdd<R extends number, C extends number>(
  a: Matrix<R, C, Complex>,
  b: Matrix<R, C, Complex>,
): Matrix<R, C, Complex> {
  return shaped<R, C>(add(a, b));
}

/** Matrix subtraction A − B (entrywise; A and B share a shape). */
export function cmatSub<R extends number, C extends number>(
  a: Matrix<R, C, Complex>,
  b: Matrix<R, C, Complex>,
): Matrix<R, C, Complex> {
  return shaped<R, C>(subtract(a, b));
}

/** Scale a matrix by a complex scalar. */
export function cmatScale<R extends number, C extends number>(
  z: Complex,
  a: Matrix<R, C, Complex>,
): Matrix<R, C, Complex> {
  return shaped<R, C>(multiply(complex(z[0], z[1]), a));
}

/** Matrix multiplication A · B: an (R×K) and a (K×C) matrix give an (R×C). */
export function cmatMul<R extends number, K extends number, C extends number>(
  a: Matrix<R, K, Complex>,
  b: Matrix<K, C, Complex>,
): Matrix<R, C, Complex> {
  return shaped<R, C>(multiply(a, b));
}

/** Transpose Aᵀ: an (R×C) matrix gives a (C×R). */
export function cmatTranspose<R extends number, C extends number>(
  a: Matrix<R, C, Complex>,
): Matrix<C, R, Complex> {
  return shaped<C, R>(transpose(a));
}

/**
 * Conjugate transpose (Hermitian adjoint) A† = Āᵀ. An (R×C) matrix gives a
 * (C×R). For real matrices this coincides with the plain transpose; A is
 * Hermitian (self-adjoint) iff A† = A.
 */
export function cmatDagger<R extends number, C extends number>(
  a: Matrix<R, C, Complex>,
): Matrix<C, R, Complex> {
  return shaped<C, R>(ctranspose(a));
}

/** Trace tr(A) = Σ Aᵢᵢ (square matrices only). */
export function cmatTrace<N extends number>(a: SquareMatrix<N, Complex>): Complex {
  squareOrderOf(a);
  // mathjs types `trace` as returning `number`, but it returns a complex value
  // for a complex matrix; bridge the (incorrect) declared type.
  return fromMathScalar(trace(a));
}

/** Lie bracket [A, B] = AB − BA. Defined on all of Mₙ(ℂ), not just GL(n,ℂ). */
export function cmatBracket<N extends number>(
  a: SquareMatrix<N, Complex>,
  b: SquareMatrix<N, Complex>,
): SquareMatrix<N, Complex> {
  squareOrderOf(a);
  squareOrderOf(b);
  return shaped<N, N>(subtract(multiply(a, b), multiply(b, a)));
}

/**
 * Determinant det(A); a singular matrix yields 0. Does not assume invertibility.
 */
export function cmatDet<N extends number>(a: SquareMatrix<N, Complex>): Complex {
  squareOrderOf(a);
  // mathjs types `det` as returning `number`, but it returns a complex value for
  // a complex matrix; bridge the (incorrect) declared type.
  return fromMathScalar(det(a));
}

/** Whether a matrix is invertible, i.e. lies in GL(n,ℂ): |det(A)| > tol. */
export function cmatIsInvertible<N extends number>(
  a: SquareMatrix<N, Complex>,
  tol = 1e-10,
): boolean {
  return cMod(cmatDet(a)) > tol;
}

/** Matrix exponential exp(A) = Σ Aⁿ / n! (square matrices only). */
export function cmatExp<N extends number>(a: SquareMatrix<N, Complex>): SquareMatrix<N, Complex> {
  squareOrderOf(a);
  return shaped<N, N>(expm(a));
}

/**
 * Eigenvalues of A, computed in ℂ. Works on any square matrix, including
 * singular ones (which simply have 0 in their spectrum); invertibility is not
 * required.
 */
export function cmatEigenvalues<N extends number>(a: SquareMatrix<N, Complex>): readonly Complex[] {
  squareOrderOf(a); // reject rectangular/ragged input with the module's own error, before mathjs
  const result = eigs(a, { eigenvectors: false });
  const values = result.values;
  const flat = (isMatrix(values) ? values.toArray() : values) as readonly (number | MathComplex)[];
  return flat.map(fromMathScalar);
}

/**
 * Whether two matrices agree to within an absolute tolerance: the Frobenius norm
 * of A − B is below `tol`. The difference and norm are computed by mathjs; only
 * the shape guard and threshold live here (mathjs's `deepEqual` has no tolerance).
 */
export function cmatEqual<R extends number, C extends number>(
  a: Matrix<R, C, Complex>,
  b: Matrix<R, C, Complex>,
  tol = 1e-10,
): boolean {
  const [ra, ca] = dimsOf(a);
  const [rb, cb] = dimsOf(b);
  if (ra !== rb || ca !== cb) {
    return false;
  }
  return (norm(subtract(a, b), "fro") as number) <= tol;
}

// ── Raw mathjs interop (brand on/off, zero cost) ───────────────

/** The underlying mathjs `Matrix` (brand stripped) — for direct mathjs interop. */
export function toMathjsMatrix<R extends number, C extends number>(
  a: Matrix<R, C, Complex>,
): MathMatrix {
  return a;
}

/** Brand a raw mathjs `Matrix` as a complex matrix of unknown shape. */
export function fromMathjsMatrix(m: MathMatrix): Matrix<number, number, Complex> {
  return shaped(m);
}

// ── n = 2 specializations (GL(2,ℂ) tangent embedding surface) ──

/** The 2×2 complex identity matrix. */
export const c2x2Identity: Complex2x2 = cmatIdentity(2);

/** Build a Complex2x2 from a real 2×2 matrix (imaginary parts zero). */
export function c2x2FromReal(
  m: readonly [readonly [number, number], readonly [number, number]],
): Complex2x2 {
  return shaped<2, 2>(cmatFromReal(m));
}

/** Multiply two Complex2x2 matrices. */
export function c2x2Mul(a: Complex2x2, b: Complex2x2): Complex2x2 {
  return shaped<2, 2>(cmatMul(a, b));
}

/** Complex trace of a 2×2 complex matrix. */
export function c2x2Trace(m: Complex2x2): Complex {
  return cmatTrace(m);
}

/** Complex determinant of a 2×2 complex matrix. */
export function c2x2Det(m: Complex2x2): Complex {
  return cmatDet(m);
}

/**
 * The two eigenvalues of a 2×2 complex matrix, computed in ℂ, so a real matrix
 * with a complex-conjugate eigenpair (e.g. a rotation block) reports its true
 * distinct eigenvalues rather than collapsing.
 */
export function c2x2Eigenvalues(m: Complex2x2): readonly [Complex, Complex] {
  const [lambda0, lambda1] = cmatEigenvalues(m);
  if (lambda0 === undefined || lambda1 === undefined) {
    throw new RangeError("c2x2Eigenvalues: a 2×2 matrix must yield two eigenvalues");
  }
  return [lambda0, lambda1];
}

/** Lie bracket [X, Y] = XY − YX for 2×2 complex matrices. */
export function c2x2Bracket(x: Complex2x2, y: Complex2x2): Complex2x2 {
  return shaped<2, 2>(cmatBracket(x, y));
}

/** Matrix exponential of a 2×2 complex matrix. */
export function c2x2Exp(x: Complex2x2): Complex2x2 {
  return shaped<2, 2>(cmatExp(x));
}
